import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Workflow, 
  RefreshCcw, 
  PlusCircle, 
  Search, 
  Zap, 
  Activity, 
  Settings, 
  ExternalLink,
  ShieldCheck,
  ChevronRight,
  Database,
  Layers,
  Cpu,
  Clock,
  CheckCircle2,
  AlertCircle,
  X
} from 'lucide-react';
import { cn } from '../lib/utils';

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string }> = 
  ({ label, value, icon, color }) => (
    <motion.div 
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
          <div className="text-3xl font-bold text-white tracking-tight">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
          {icon}
        </div>
      </div>
    </motion.div>
  );

const N8nWorkflowManagementScreen: React.FC = () => {
  const [availableWorkflows, setAvailableWorkflows] = useState<any[]>([]);
  const [tenantWorkflows, setTenantWorkflows] = useState<any[]>([]);
  const [executions, setExecutions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'history'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [configModal, setConfigModal] = useState<{ show: boolean; workflow: any | null }>({
    show: false,
    workflow: null
  });
  const [configData, setConfigData] = useState<string>('{}');
  // In-modal feedback: a failed enable must be visible WHERE the admin is
  // looking, not in a banner behind the modal.
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSaving, setModalSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [availableRes, tenantRes, executionsRes] = await Promise.all([
        axios.get('/api/n8n/workflows/available'),
        axios.get('/api/n8n/workflows/tenant'),
        axios.get('/api/n8n/workflows/executions')
      ]);
      setAvailableWorkflows(Array.isArray(availableRes.data) ? availableRes.data : []);
      setTenantWorkflows(Array.isArray(tenantRes.data) ? tenantRes.data : []);
      setExecutions(Array.isArray(executionsRes.data) ? executionsRes.data : []);
    } catch (err: any) {
      console.error('Failed to fetch n8n data:', err);
      setError(err.response?.data?.message || 'Failed to connect to Workflow Engine');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getWorkflowStatus = (workflowKey: string) => {
    return tenantWorkflows.find(tw => tw.workflow?.workflowKey === workflowKey);
  };

  const handleToggle = async (workflow: any, currentlyEnabled: boolean) => {
    if (!currentlyEnabled) {
      // Open config modal to enable
      const status = getWorkflowStatus(workflow.workflowKey);
      setConfigData(status?.configuration ? JSON.stringify(status.configuration, null, 2) : '{}');
      setConfigModal({ show: true, workflow });
    } else {
      // Disable immediately
      try {
        await axios.post(`/api/n8n/workflows/tenant/disable/${workflow.workflowKey}`);
        setSuccessMessage(`Workflow "${workflow.workflowName}" disabled`);
        fetchData();
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to disable workflow');
      }
    }
  };

  const handleSaveConfig = async () => {
    if (!configModal.workflow) return;
    setModalError(null);
    setModalSaving(true);
    try {
      const config = JSON.parse(configData);
      await axios.post(`/api/n8n/workflows/tenant/enable/${configModal.workflow.workflowKey}`, config);
      setSuccessMessage(`Workflow "${configModal.workflow.workflowName}" enabled and configured`);
      setConfigModal({ show: false, workflow: null });
      fetchData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        setModalError('Invalid JSON configuration format');
      } else {
        setModalError(err.response?.data?.message || 'Failed to save configuration');
      }
    } finally {
      setModalSaving(false);
    }
  };

  const filteredWorkflows = useMemo(() => {
    let result = availableWorkflows;
    
    if (activeTab === 'active') {
      result = result.filter(w => getWorkflowStatus(w.workflowKey)?.isEnabled);
    } else if (activeTab === 'history') {
      return result; // Table view handles history
    }
    
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(w => 
        w.workflowName?.toLowerCase().includes(s) || 
        w.workflowDescription?.toLowerCase().includes(s) ||
        w.workflowKey?.toLowerCase().includes(s)
      );
    }
    return result;
  }, [availableWorkflows, tenantWorkflows, activeTab, searchTerm]);

  const stats = useMemo(() => {
    const active = tenantWorkflows.filter(tw => tw.isEnabled).length;
    const totalExecs = executions.length;
    const recentExecs = executions.filter(e => {
      const start = new Date(e.startedAt);
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return start > yesterday;
    }).length;
    const successRate = executions.length > 0 
      ? (executions.filter(e => e.status === 'success').length / executions.length * 100).toFixed(1)
      : '100';

    return { active, totalExecs, recentExecs, successRate };
  }, [tenantWorkflows, executions]);

  // 'automation'/'notification' carry the document-ingestion workflows (email
  // intake etc.) — they were registered in V242 but never rendered here, which
  // made the email lane un-enableable from the tenant surface.
  const categories = ['PHASE_1', 'PHASE_2', 'PHASE_3', 'automation', 'notification'];
  const categoryLabel = (c: string) =>
    c === 'automation' ? 'DOCUMENT AUTOMATION'
      : c === 'notification' ? 'NOTIFICATIONS'
      : c.replace('_', ' ');

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <Workflow className="w-8 h-8 text-blue-500" />
                Workflow Orchestrator
              </h1>
              <p className="text-slate-500 font-medium mt-1">Institutional automation engine powered by n8n.io hooks and triggers</p>
            </div>
            <div className="flex gap-3">
              <div className="flex bg-slate-900 border border-white/10 p-1 rounded-xl">
                {['all', 'active', 'history'].map((tab) => (
                  <button 
                    key={tab}
                    onClick={() => setActiveTab(tab as any)}
                    className={cn(
                      "px-4 py-1.5 rounded-lg text-xs font-bold transition-all capitalize", 
                      activeTab === tab ? "bg-blue-600 text-white shadow-lg" : "text-slate-500 hover:text-slate-300"
                    )}
                  >
                    {tab === 'history' ? 'Execution History' : tab}
                  </button>
                ))}
              </div>
              <button 
                onClick={fetchData}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
              >
                <RefreshCcw className={cn("w-4 h-4", isLoading && "animate-spin")} />
                Refresh
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard label="Total Templates" value={String(availableWorkflows.length)} icon={<Workflow className="w-5 h-5" />} color="#3b82f6" />
            <StatCard label="Active Workflows" value={String(stats.active)} icon={<Zap className="w-5 h-5" />} color="#10b981" />
            <StatCard label="Executions (24h)" value={String(stats.recentExecs)} icon={<Activity className="w-5 h-5" />} color="#8b5cf6" />
            <StatCard label="Success Rate" value={`${stats.successRate}%`} icon={<ShieldCheck className="w-5 h-5" />} color="#f59e0b" />
          </div>
        </motion.div>

        {successMessage && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-3 text-emerald-400 font-bold">
            <CheckCircle2 className="w-5 h-5" /> {successMessage}
          </motion.div>
        )}

        {error && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400 font-bold">
            <AlertCircle className="w-5 h-5" /> {error}
          </motion.div>
        )}

        {activeTab === 'history' ? (
          <GlassCard className="flex flex-col min-h-[500px]">
            <div className="p-4 border-b border-white/5 bg-slate-900/30">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-400" />
                Execution History
              </h3>
            </div>
            <div className="flex-1 overflow-auto">
              <table className="w-full text-left">
                <thead className="sticky top-0 bg-slate-900 z-10">
                  <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                    <th className="py-4 px-6">Execution ID</th>
                    <th className="py-4 px-6">Workflow</th>
                    <th className="py-4 px-6">Trigger</th>
                    <th className="py-4 px-6">Status</th>
                    <th className="py-4 px-6">Started At</th>
                    <th className="py-4 px-6 text-right">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {executions.map((e, i) => {
                    const wf = availableWorkflows.find(w => w.id === e.workflowId);
                    return (
                      <tr key={i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                        <td className="py-4 px-6 font-mono text-xs text-slate-400">{e.executionId || '---'}</td>
                        <td className="py-4 px-6">
                          <div className="font-bold text-white">{wf?.workflowName || 'Unknown'}</div>
                          <div className="text-[10px] text-slate-500">{wf?.workflowKey}</div>
                        </td>
                        <td className="py-4 px-6">
                          <span className="px-2 py-0.5 rounded bg-slate-800 text-[10px] font-bold uppercase text-slate-400">
                            {e.triggerType}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <span className={cn(
                            "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider",
                            e.status === 'success' ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"
                          )}>
                            {e.status}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-slate-400 text-xs">
                          {new Date(e.startedAt).toLocaleString()}
                        </td>
                        <td className="py-4 px-6 text-right font-mono text-xs text-slate-500">
                          {e.executionTimeMs ? `${e.executionTimeMs}ms` : '---'}
                        </td>
                      </tr>
                    );
                  })}
                  {executions.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-24 text-center text-slate-500 italic">No execution history found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </GlassCard>
        ) : (
          <div className="space-y-12">
            {categories.map(category => {
              const categoryWorkflows = filteredWorkflows.filter(w => w.workflowCategory === category);
              if (categoryWorkflows.length === 0) return null;

              return (
                <div key={category} className="space-y-6">
                  <div className="flex items-center gap-4">
                    <h2 className="text-sm font-bold text-slate-400 uppercase tracking-[0.2em]">{categoryLabel(category)}</h2>
                    <div className="h-px bg-white/5 flex-1" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {categoryWorkflows.map((w, i) => {
                      const status = getWorkflowStatus(w.workflowKey);
                      const isEnabled = status?.isEnabled || false;
                      
                      return (
                        <motion.div key={i} variants={fadeInUp} initial="initial" animate="animate">
                          <GlassCard className="p-6 h-full flex flex-col group border border-white/5 hover:border-blue-500/30 transition-all">
                            <div className="flex justify-between items-start mb-6">
                              <div className={cn(
                                "w-12 h-12 rounded-2xl flex items-center justify-center transition-colors shadow-lg",
                                isEnabled ? "bg-emerald-500/10 text-emerald-400" : "bg-slate-800 text-slate-500"
                              )}>
                                <Cpu className="w-6 h-6" />
                              </div>
                              <div className="flex items-center gap-3">
                                <span className={cn(
                                  "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest",
                                  isEnabled ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-slate-800 text-slate-500"
                                )}>
                                  {isEnabled ? 'Enabled' : 'Disabled'}
                                </span>
                                <div 
                                  onClick={() => handleToggle(w, isEnabled)}
                                  className={cn(
                                    "w-10 h-5 rounded-full p-1 cursor-pointer transition-all duration-300",
                                    isEnabled ? "bg-emerald-600" : "bg-slate-700"
                                  )}
                                >
                                  <motion.div 
                                    animate={{ x: isEnabled ? 20 : 0 }}
                                    className="w-3 h-3 bg-white rounded-full shadow-sm"
                                  />
                                </div>
                              </div>
                            </div>
                            
                            <h3 className="text-lg font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">{w.workflowName}</h3>
                            <p className="text-xs text-slate-500 leading-relaxed mb-8 flex-1">{w.workflowDescription}</p>
                            
                            <div className="pt-6 border-t border-white/5 flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500 uppercase">
                                  <Clock className="w-3 h-3" /> {status?.lastExecutedAt ? new Date(status.lastExecutedAt).toLocaleDateString() : 'Never'}
                                </div>
                                {isEnabled && (
                                  <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-400 uppercase">
                                    <CheckCircle2 className="w-3 h-3" /> {status?.executionCount || 0} Runs
                                  </div>
                                )}
                              </div>
                              {isEnabled && (
                                <button 
                                  onClick={() => {
                                    setConfigData(status?.configuration ? JSON.stringify(status.configuration, null, 2) : '{}');
                                    setConfigModal({ show: true, workflow: w });
                                  }}
                                  className="p-2 rounded-lg bg-white/5 text-slate-400 hover:text-white hover:bg-blue-600 transition-all shadow-xl"
                                  title="Configure"
                                >
                                  <Settings className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </GlassCard>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Configuration Modal */}
      <AnimatePresence>
        {configModal.show && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-slate-950/80 backdrop-blur-md" onClick={() => setConfigModal({ show: false, workflow: null })} />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="relative w-full max-w-lg bg-slate-900 border border-white/10 rounded-3xl shadow-2xl overflow-hidden">
              <div className="p-8 border-b border-white/5 bg-slate-950/30 flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-white tracking-tight">Configure Workflow</h2>
                  <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-1">{configModal.workflow?.workflowName}</p>
                </div>
                <button onClick={() => setConfigModal({ show: false, workflow: null })} className="p-2 text-slate-500 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-8 space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Configuration (JSON)</label>
                  <textarea 
                    value={configData}
                    onChange={e => setConfigData(e.target.value)}
                    className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all min-h-[200px] font-mono"
                    placeholder='{"key": "value"}'
                  />
                  <p className="text-[10px] text-slate-600 italic">Enter the required parameters for this workflow in JSON format.</p>
                </div>
                {modalError && (
                  <div className="px-4 py-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-300 text-xs leading-relaxed">
                    {modalError}
                  </div>
                )}
                <div className="flex gap-4">
                  <button onClick={() => { setConfigModal({ show: false, workflow: null }); setModalError(null); }} className="flex-1 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-2xl transition-all">Cancel</button>
                  <button onClick={handleSaveConfig} disabled={modalSaving} className="flex-1 py-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-wait text-white font-bold rounded-2xl transition-all shadow-xl shadow-blue-600/20">
                    {modalSaving ? 'Deploying workflow…'
                      : getWorkflowStatus(configModal.workflow?.workflowKey)?.isEnabled ? 'Update Config' : 'Enable & Save'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default N8nWorkflowManagementScreen;
